"use client";

import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type PaginationLinkSize =
  | "default"
  | "xs"
  | "sm"
  | "lg"
  | "icon"
  | "icon-xs"
  | "icon-sm"
  | "icon-lg";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  const { t } = useTranslation();

  return (
    <nav
      role="navigation"
      aria-label={props["aria-label"] ?? t("common.pagination.label")}
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({ className, ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" className={cn("", className)} {...props} />;
}

function PaginationLink({
  className,
  disabled,
  isActive,
  size = "icon-sm",
  tabIndex,
  ...props
}: React.ComponentProps<"a"> & {
  disabled?: boolean;
  isActive?: boolean;
  size?: PaginationLinkSize;
}) {
  return (
    <a
      aria-current={isActive ? "page" : undefined}
      aria-disabled={disabled || undefined}
      data-active={isActive}
      data-slot="pagination-link"
      tabIndex={disabled ? -1 : tabIndex}
      className={cn(
        buttonVariants({
          variant: isActive ? "secondary" : "ghost",
          size,
        }),
        disabled && "pointer-events-none cursor-default opacity-50",
        className
      )}
      {...props}
    />
  );
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useTranslation();

  return (
    <PaginationLink
      aria-label={props["aria-label"] ?? t("common.pagination.previousAriaLabel")}
      className={cn("gap-1 px-2.5", className)}
      size="sm"
      {...props}
    >
      <ChevronLeft className="size-4" aria-hidden="true" />
      <span>{t("common.pagination.previous")}</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useTranslation();

  return (
    <PaginationLink
      aria-label={props["aria-label"] ?? t("common.pagination.nextAriaLabel")}
      className={cn("gap-1 px-2.5", className)}
      size="sm"
      {...props}
    >
      <span>{t("common.pagination.next")}</span>
      <ChevronRight className="size-4" aria-hidden="true" />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<"span">) {
  const { t } = useTranslation();

  return (
    <span
      data-slot="pagination-ellipsis"
      className={cn("flex size-8 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" aria-hidden="true" />
      <span className="sr-only">{t("common.pagination.morePages")}</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
